<template>
  <FloatingPanel 
    v-if="(documentsSignPending.length) && rotaPermitida()" 
    title="Documentos para assinatura" 
    subtitle="Documentos aguardando sua assinatura digital" 
    :badge="documentsSignPending.length" 
    :collapsed="collapsed" 
    :closable="closable" 
    :position-index="positionIndex" 
    :width="410" 
    :max-body-height="480" 
    @update:collapsed="collapsed = $event" 
    @close="$emit('close')"
  >
    <template #icon>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M7 3H14L19 8V20C19 20.6 18.6 21 18 21H7C6.4 21 6 20.6 6 20V4C6 3.4 6.4 3 7 3Z"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linejoin="round"
        />

        <path
          d="M14 3V8H19"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linejoin="round"
        />

        <path
          d="M9 15L11 17L15.5 12.5"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </template>

    <template #header-actions>
      <button
        v-if="showRefresh"
        type="button"
        class="refresh-button"
        :class="{
          'refresh-button--loading': loading
        }"
        aria-label="Atualizar documentos"
        :disabled="loading"
        @click="refresh"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M20 7V12H15"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />

          <path
            d="M19 12C18.5 8.7 15.6 6 12 6C8.1 6 5 9.1 5 13C5 16.9 8.1 20 12 20C14.6 20 16.9 18.6 18.1 16.5"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </template>

    <!-- Loading -->
    <div
      v-if="loading && !documentsSignPending.length"
      class="panel-state"
    >
      <div class="spinner" />

      <span>
        Buscando documentos...
      </span>
    </div>

    <!-- Sem documentos -->
    <div
      v-else-if="!documentsSignPending.length"
      class="panel-state panel-state--empty"
    >
      <div class="empty-icon">
        <svg
          viewBox="0 0 32 32"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="16"
            cy="16"
            r="13"
            stroke="currentColor"
            stroke-width="2"
          />

          <path
            d="M10.5 16L14 19.5L21.5 12"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <strong>Nenhum documento pendente</strong>

      <span>
        Os novos documentos aparecerão aqui quando
        estiverem prontos para assinatura.
      </span>
    </div>

    <!-- Documentos -->
    <div
      v-else
      class="documents"
    >
      <article
        v-for="documentSignPending in documentsSignPending" 
        :key="documentSignPending.ID_PACIENTE_LAUDO" 
        class="document-item"
      >
        <div class="document-item__top">
          <div class="document-item__icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7 3H14L19 8V20C19 20.6 18.6 21 18 21H7C6.4 21 6 20.6 6 20V4C6 3.4 6.4 3 7 3Z"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />

              <path
                d="M14 3V8H19"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />

              <path
                d="M9 12H16M9 15H15"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>
          </div>

          <div class="document-item__content">
            <div class="document-item__title">
              {{ documentSignPending.TITULO }}
            </div>

            <div
              v-if="documentSignPending.PACIENTE"
              class="document-item__patient"
            >
              {{ documentSignPending.PACIENTE }}
            </div>
          </div>

          <!--<span
            class="document-item__type"
          >
            item.type
          </span>-->
          <span
            class="document-item__type"
          >
            Documento
          </span>
        </div>

        <div
          v-if="documentSignPending.PRODUTO || documentSignPending.DATA_INCLUSAO"
          class="document-item__metadata"
        >
          <span v-if="documentSignPending.PRODUTO">
            {{ documentSignPending.PRODUTO }}
          </span>

          <span
            v-if="documentSignPending.DATA_INCLUSAO"
            class="document-item__date"
          >
            {{ formatDate(documentSignPending.DATA_INCLUSAO) }}
          </span>
        </div>

        <div class="document-item__actions">
          <button
            type="button"
            class="action-button action-button--discard"
            :disabled="isItemBusy(documentSignPending)"
            @click="descartar(documentSignPending)"
          >
            <svg 
              viewBox="0 0 20 20" 
              fill="none" 
              aria-hidden="true"
            >
              <path
                d="M4 6H16M8 6V4H12V6M6 6L7 16H13L14 6"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            Descartar
          </button>

          <button
            type="button"
            class="action-button action-button--sign"
            :disabled="isItemBusy(documentSignPending)"
            @click="assinar(documentSignPending)"
          >
            <span
              v-if="documentSignPending.signing"
              class="button-spinner"
            />

            <svg
              v-else
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 15.5C5.8 14.4 7.4 13.9 9 14C10.7 14.1 11.7 15 13.3 14.5C14.3 14.2 15.2 13.5 16 12.5"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />

              <path
                d="M6 13L13.8 5.2C14.5 4.5 15.6 4.5 16.3 5.2C17 5.9 17 7 16.3 7.7L8.5 15.5L5 16L6 13Z"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
            </svg>

            {{ documentSignPending.signing ? 'Assinando...' : 'Assinar' }}
          </button>
        </div>
      </article>
    </div>

    <template
      v-if="documentsSignPending.length"
      #footer
    >
      <div class="panel-footer">
        <span>
          {{ documentsSignPending.length }}
          {{ documentsSignPending.length === 1 ? 'documento pendente' : 'documentos pendentes' }}
        </span>

        <span
          v-if="loading"
          class="panel-footer__sync"
        >
          Atualizando...
        </span>
      </div>
    </template>
    <p v-if="erroConsulta" role="alert" class="text-warning px-3">{{ erroConsulta }}</p>
    <ModalAssinaturaDigital 
      ref="modalAssinaturaDigital" 
      @sairModal="sairModalAssinatura"
    />
  </FloatingPanel>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import FloatingPanel from "@/../../desk-spirit/src/components/FloatingPanel.vue";
import ModalAssinaturaDigital from "@/../../desk-spirit/src/components/modals/ModalAssinaturaDigital.vue";

export default {
  name: "PendingSignaturesPanel",

  components: {
    FloatingPanel, 
    ModalAssinaturaDigital
  },

  data() {
    return {
      checkDocumentsSign: null, 
      collapsed: true, 
      loading: false,
      erroConsulta: "",
      encerrado: false,
      versaoConsulta: 0
    };
  },

  props: {
    closable: {
      type: Boolean,
      default: false
    },

    showRefresh: {
      type: Boolean,
      default: true
    },

    positionIndex: {
      type: Number,
      default: 0
    }
  },

  emits: [
    'sign',
    'discard',
    'refresh',
    'close'
  ], 

  mounted() {
    this.startTimer();
  }, 

  beforeUnmount() {
    this.encerrado = true;
    this.clearTimer();
  }, 

  computed: {
    ...mapGetters([
      "documentsSignPending",
      "medicoUsuario"
    ])
  },

  methods: {
    ...mapActions([
      "checkDocumentsSignPending", 
      "atualizarDocumentSigning", 
      "atualizarDocumentDiscarding", 
      "discardDocumentSignPending"
    ]), 
    isItemBusy(item) {
      return Boolean(
        item.signing ||
        item.discarding
      );
    },

    formatDate(value) {
      if (!value) {
        return "";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return new Intl.DateTimeFormat(
        "pt-BR", 
        {
            day: "2-digit", 
            month: "2-digit", 
            hour: "2-digit", 
            minute: "2-digit"
        }
      ).format(date);
    }, 
    buscar() {
      const versao = this.versaoConsulta;
      this.checkDocumentsSign = setTimeout(async () => {
        try {
          if(this.medicoUsuario.ID_MEDICO) { await this.checkDocumentsSignPending(); }
          if(!this.encerrado && versao === this.versaoConsulta) { this.erroConsulta = ""; }
        } catch(error) {
          if(!this.encerrado && versao === this.versaoConsulta) { this.erroConsulta = "Não foi possível atualizar as pendências de assinatura."; }
        } finally { if(!this.encerrado && versao === this.versaoConsulta) { this.buscar(); } }
      }, 15000);
    }, 
    async refresh() {
      this.clearTimer();
      const versao = this.versaoConsulta;
      this.loading = true;
      try {
        if(this.medicoUsuario.ID_MEDICO) { await this.checkDocumentsSignPending(); }
        if(!this.encerrado && versao === this.versaoConsulta) { this.erroConsulta = ""; }
      } catch(error) {
        if(!this.encerrado && versao === this.versaoConsulta) { this.erroConsulta = "Não foi possível atualizar as pendências de assinatura."; }
      } finally {
        if(!this.encerrado && versao === this.versaoConsulta) { this.loading = false; this.startTimer(); }
      }
    }, 
    resetTimer() {
      this.clearTimer();
      this.startTimer();
    }, 
    startTimer() {
      this.buscar();
    }, 
    clearTimer() {
      this.versaoConsulta++;
      if(this.checkDocumentsSign) {
        clearInterval(this.checkDocumentsSign);
      }
    }, 
    assinar(documentSignPending) {
      this.atualizarDocumentSigning(documentSignPending.ID_PACIENTE_LAUDO);

      this.$refs.modalAssinaturaDigital.idPacienteLaudo = documentSignPending.ID_PACIENTE_LAUDO;
      this.$refs.modalAssinaturaDigital.toggleModal();
    }, 
    async descartar(documentSignPending) {
      this.atualizarDocumentDiscarding(documentSignPending.ID_PACIENTE_LAUDO);
      await this.discardDocumentSignPending(documentSignPending.ID_PACIENTE_LAUDO);
      this.refresh();
    }, 
    sairModalAssinatura(idPacienteLaudo) {
      if(idPacienteLaudo) {
        this.atualizarDocumentSigning(idPacienteLaudo);
      }
      this.refresh();
    }, 
    rotaPermitida() {
      if(["totemPaciente", "chamadaNaTV"].includes(this.$route.name)) {
        return false;
      } else {
        return true;
      }
    }
  }
};
</script>

<style scoped>
.refresh-button {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;

  padding: 0;

  color: #657286;
  background: transparent;

  border: 0;
  border-radius: 8px;

  cursor: pointer;
}

.refresh-button:hover:not(:disabled) {
  color: #0058b7;
  background: #eef4ff;
}

.refresh-button:disabled {
  cursor: default;
  opacity: 0.6;
}

.refresh-button svg {
  width: 17px;
  height: 17px;
}

.refresh-button--loading svg {
  animation: rotate 1s linear infinite;
}

/* States */

.panel-state {
  display: flex;
  align-items: center;
  justify-content: center;

  flex-direction: column;

  min-height: 190px;

  padding: 30px;

  gap: 12px;

  color: #758196;

  text-align: center;

  font-size: 13px;
}

.panel-state--empty strong {
  color: #2a3548;

  font-size: 14px;
}

.panel-state--empty > span {
  max-width: 270px;

  line-height: 1.5;
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 50px;
  height: 50px;

  color: #20945b;
  background: #eaf8f0;

  border-radius: 15px;
}

.empty-icon svg {
  width: 29px;
  height: 29px;
}

/* Lista */

.documents {
  padding: 8px;
}

.document-item {
  padding: 14px;

  background: #ffffff;

  border: 1px solid #e6ebf0;
  border-radius: 11px;

  transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;
}

.document-item + .document-item {
  margin-top: 8px;
}

.document-item:hover {
  border-color: #d2dae3;

  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.04);
}

.document-item__top {
  display: flex;
  align-items: flex-start;

  gap: 10px;
}

.document-item__icon {
  display: flex;
  align-items: center;
  justify-content: center;

  flex: 0 0 auto;

  width: 36px;
  height: 36px;

  color: #0058b7;
  background: #eef4ff;

  border-radius: 9px;
}

.document-item__icon svg {
  width: 20px;
  height: 20px;
}

.document-item__content {
  flex: 1;

  min-width: 0;
}

.document-item__title {
  overflow: hidden;

  color: #1e293b;

  font-size: 13px;
  font-weight: 700;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-item__patient {
  margin-top: 3px;

  overflow: hidden;

  color: #657286;

  font-size: 12px;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-item__type {
  flex: 0 0 auto;

  padding: 4px 7px;

  color: #536173;
  background: #f2f5f8;

  border-radius: 6px;

  font-size: 10px;
  font-weight: 700;

  text-transform: uppercase;
}

.document-item__metadata {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 8px;

  margin-top: 10px;
  padding-top: 9px;

  color: #8490a1;

  border-top: 1px solid #f0f2f5;

  font-size: 10px;
}

.document-item__date {
  flex: 0 0 auto;
}

/* Actions */

.document-item__actions {
  display: flex;
  justify-content: flex-end;

  gap: 7px;

  margin-top: 12px;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-height: 34px;

  padding: 0 11px;

  gap: 6px;

  border-radius: 7px;

  cursor: pointer;

  font-size: 11px;
  font-weight: 700;

  transition:
      color 0.15s ease,
      background-color 0.15s ease,
      border-color 0.15s ease;
}

.action-button:disabled {
  cursor: default;
  opacity: 0.55;
}

.action-button svg {
  width: 15px;
  height: 15px;
}

.action-button--discard {
  color: #687588;
  background: #ffffff;

  border: 1px solid #d8dee6;
}

.action-button--discard:hover:not(:disabled) {
  color: #c93434;
  background: #fff5f5;

  border-color: #f1bcbc;
}

.action-button--sign {
  color: #ffffff;
  background: #0058b7;

  border: 1px solid #0058b7;
}

.action-button--sign:hover:not(:disabled) {
  background: #0048b7;
  border-color: #0048b7;
}

/* Footer */

.panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;

  color: #788497;

  font-size: 10px;
}

.panel-footer__sync {
  color: #0058b7;
}

/* Loading */

.spinner,
.button-spinner {
  border-style: solid;
  border-radius: 50%;

  animation: rotate 0.8s linear infinite;
}

.spinner {
  width: 23px;
  height: 23px;

  border-width: 2px;
  border-color: #d5deea;
  border-top-color: #0058b7;
}

.button-spinner {
  width: 13px;
  height: 13px;

  border-width: 2px;
  border-color: rgba(255,255,255,.4);
  border-top-color: #ffffff;
}

@keyframes rotate {
  to {
    transform: rotate(360deg);
  }
}
</style>
